// App — shell, state, routing.
// Tabs live at /, /discover, /saved, /done, /friends, /profile.
// Deep links: /a/:placeId (place card, public when signed out),
// /p/:planId (plan detail), /invite/:code (friend invite redemption).

import React from 'react';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';
import { LOCATIONS, DEFAULT_LOCATION_ID } from './data.js';
import { HomeBaseContext } from './context.js';
import { BottomTabs, Toast, HeroArt, LarkleMark } from './components.jsx';
import { VibeScreen, DiscoverScreen } from './screens-main.jsx';
import { SavedScreen, FriendsScreen, ProfileScreen, ShareInboxSheet } from './screens-lists.jsx';
import { DetailModal, ShareSheet, RateSheet, LocationPicker, PlanDetailSheet } from './modals.jsx';
import { ChatThreadSheet } from './chat.jsx';
import { vibeToApiPrefs, apiPrefsToVibe } from './lib/prefs.js';
import { milesFrom, haversineMiles } from './lib/geo.js';
import { guessIPLocation } from './lib/ipgeo.js';
import { matchScore } from './lib/match.js';
import { disablePush } from './lib/push.js';
import { trackPageview, trackEvent, setAnalyticsArea, placeKey } from './lib/analytics.js';
import {
  larkleInitAuth, larkleSignIn, larkleSignOut, larkleOnAuth, larkleGetUid, larkleGetUser,
  signInErrorMessage,
  apiFetchNearby, apiFetchPublicNearby, apiMarkViewed, apiFetchViewed, apiFetchLocation, apiFetchPublicLocation,
  apiFetchPlaceReviews,
  apiGetPreferences, apiUpdatePreferences, apiAddReview, apiDeleteAccount, apiSyncProfile,
  apiFetchFriends, apiFetchIncomingRequests, apiFetchPlans, apiGetPlan, apiCreatePlan,
  apiSendShare, apiFetchShareInbox, apiRedeemInvite, apiFetchConversations,
  apiFetchMyViews, apiFetchFriendsActivity,
  placeToActivity, publicPlaceToActivity, placeToLocationView, viewToDoneEntry,
} from './lib/api.js';

// ── Per-user persisted state ─────────────────────────────────────────────────
// Namespaced by uid so accounts on the same device don't bleed into each
// other; versioned so future shape changes can migrate instead of crash.
const SCHEMA_VERSION = 5;
const LEGACY_KEY = 'vibe-finder-state-v4';
const stateKey = (uid) => `larkle-state:${uid || 'anon'}`;

// Local-only preferences — vibe/prefs/locationId are genuinely client-side
// concepts with no server equivalent. Content (saved places, done/passport
// entries) is NOT persisted here; it's hydrated fresh from the server on
// every load (see the fetch effects below), since it's the authoritative
// source and a locally-cached copy would only go stale.
const DEFAULT_STATE = {
  _v: SCHEMA_VERSION,
  vibe: { physical: 50, social: 50, outdoor: 50, newPeople: 50, creative: 50, boozy: 20, eating: 50, expensive: 30, nsfw: 0, fun: 70 },
  prefs: { price: 2, distance: 2, showRevisits: true },
  locationId: DEFAULT_LOCATION_ID,
};

function loadState(uid) {
  try {
    const raw = localStorage.getItem(stateKey(uid));
    if (raw) {
      const s = JSON.parse(raw);
      if (s._v === SCHEMA_VERSION) return { ...DEFAULT_STATE, ...s };
      // future migrations go here, keyed on s._v
    }
  } catch {}
  // one-time migration from the old shared (pre-namespace) key
  try {
    const old = localStorage.getItem(LEGACY_KEY);
    if (old) {
      const s = JSON.parse(old);
      localStorage.removeItem(LEGACY_KEY);
      return {
        ...DEFAULT_STATE,
        vibe: s.vibe || DEFAULT_STATE.vibe,
        prefs: { ...DEFAULT_STATE.prefs, ...(s.prefs || {}) },
        locationId: s.locationId || DEFAULT_STATE.locationId,
      };
    }
  } catch {}
  return DEFAULT_STATE;
}

const TAB_PATHS = { vibe: '/', discover: '/discover', saved: '/saved', friends: '/friends', profile: '/profile' };

// Guests can't widen their radius (that lives behind the Vibe tab), so their
// deck fetches wider than the signed-in default — a sparse first deck for a
// visitor with no way to fix it would end the visit.
const GUEST_DISTANCE_MI = 5;

// What the guest deck ranks toward: restaurants, bars, going-out spots — the
// most broadly appealing stack for a visitor we know nothing about. Mirrors
// PUBLIC_FEED_PREFERENCES on the server (0-10 scale, publicController.ts),
// which picks the categories this same feed is fetched from — keep the two in
// sync or the % match chips will contradict the card order.
const GUEST_VIBE = { physical: 30, social: 80, outdoor: 40, newPeople: 60, creative: 40, boozy: 70, eating: 90, expensive: 50, nsfw: 0, fun: 100 };
function tabFromPath(path) {
  if (path.startsWith('/discover')) return 'discover';
  if (path.startsWith('/saved')) return 'saved';
  if (path.startsWith('/done')) return 'saved'; // passport = "Been there" segment of Saved
  if (path.startsWith('/friends') || path.startsWith('/p/') || path.startsWith('/c/')) return 'friends';
  if (path.startsWith('/profile')) return 'profile';
  if (path.startsWith('/a/')) return 'discover';
  return 'vibe';
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  // SPA route changes don't trigger a real navigation, so gtag's automatic
  // pageview (disabled in initAnalytics) has to be replaced with this.
  React.useEffect(() => { trackPageview(path); }, [path]);

  // ── auth ───────────────────────────────────────────────────────────────────
  const [authUser, setAuthUser] = React.useState(null);
  // false until Firebase resolves the session — the gate shows a splash, not
  // a login flash, for already-signed-in users.
  const [authReady, setAuthReady] = React.useState(false);
  React.useEffect(() => {
    larkleOnAuth(state => {
      setAuthUser(state);
      setAuthReady(true);
      if (state) {
        // larkleOnAuth only fires on a genuine sign-in/out/uid change (not on
        // silent token refresh), so this can't double-count — see api.js.
        const fbUser = larkleGetUser();
        const isNewUser = fbUser?.metadata?.creationTime === fbUser?.metadata?.lastSignInTime;
        trackEvent(isNewUser ? 'sign_up' : 'login', { method: 'google' });
      }
    });
    larkleInitAuth();
    const t = setTimeout(() => setAuthReady(true), 3000); // belt-and-braces
    return () => clearTimeout(t);
  }, []);
  const uid = authUser?.uid || null;

  // ── persisted state (per uid) ─────────────────────────────────────────────
  const initial = React.useMemo(() => loadState(null), []);
  const [vibe, setVibe] = React.useState(initial.vibe);
  const [prefs, setPrefs] = React.useState(initial.prefs);
  const [locationId, setLocationId] = React.useState(initial.locationId);
  // Not persisted — hydrated fresh from the server whenever authUser is set
  // (see the "Hydrate the done/passport list" effect below).
  const [doneEntries, setDoneEntries] = React.useState([]);

  // When the signed-in user changes, swap to that user's bucket
  const _uidRef = React.useRef(null);
  React.useEffect(() => {
    if (_uidRef.current === uid) return;
    _uidRef.current = uid;
    let bucketFresh = false;
    try { bucketFresh = !localStorage.getItem(stateKey(uid)); } catch {}
    const s = loadState(uid);
    setVibe(s.vibe); setPrefs(s.prefs); setLocationId(s.locationId);
    // A fresh bucket (first sign-in on this device) starts at the NYC
    // default — carry this session's IP guess over instead of snapping the
    // just-converted guest back to Times Square.
    if (bucketFresh && _geoGuessRef.current) applyGeoGuess(_geoGuessRef.current);
  }, [uid]);

  React.useEffect(() => {
    try {
      localStorage.setItem(stateKey(uid), JSON.stringify({
        _v: SCHEMA_VERSION, vibe, prefs, locationId,
      }));
    } catch {}
  }, [uid, vibe, prefs, locationId]);

  // ── UI state ───────────────────────────────────────────────────────────────
  const tab = tabFromPath(path);
  const setTab = (t) => navigate(TAB_PATHS[t] || '/');
  const [detailId, setDetailId] = React.useState(null);
  const [shareId, setShareId] = React.useState(null);
  const [shareMode, setShareMode] = React.useState('quick'); // which ShareSheet tab opens
  const [rateId, setRateId] = React.useState(null);
  const [openPlan, setOpenPlan] = React.useState(null); // full plan object
  const [locPickerOpen, setLocPickerOpen] = React.useState(false);
  const [inboxOpen, setInboxOpen] = React.useState(false);
  const [toast, setToast] = React.useState('');
  const [fetchedDetail, setFetchedDetail] = React.useState(null); // deep-linked place
  // Guest sign-in gate: { activity|null, reason: 'save'|'share'|'vibe' }.
  // Opens at the moment of intent (right-swipe, share, vibe tweak) instead of
  // gating the whole app at the door.
  const [guestGate, setGuestGate] = React.useState(null);

  // ── API state ──────────────────────────────────────────────────────────────
  const [apiActivities, setApiActivities] = React.useState(null);
  const [apiStatus, setApiStatus] = React.useState('idle');
  const [apiError, setApiError] = React.useState(null);
  const [savedActivities, setSavedActivities] = React.useState([]);
  const [savedLoading, setSavedLoading] = React.useState(false);
  const [savedError, setSavedError] = React.useState(null);
  const [savedHasMore, setSavedHasMore] = React.useState(false);
  const [savedOffset, setSavedOffset] = React.useState(0);
  const [savedPaginating, setSavedPaginating] = React.useState(false);
  const [savedFilters, setSavedFilters] = React.useState({ query: '', coords: null, radius: null });
  const [nearbyRefreshKey, setNearbyRefreshKey] = React.useState(0);
  const [customGPS, setCustomGPS] = React.useState(null); // { lat, lon } — arbitrary map pin
  // Cards swiped away this session — always removes the card from the deck,
  // even with showRevisits on (that option controls what the server fetches,
  // not whether a swipe dismisses). Session-local by design (not persisted);
  // reset on fresh fetch.
  const [dismissedIds, setDismissedIds] = React.useState([]);
  const [paginationToken, setPaginationToken] = React.useState(null);
  const [paginating, setPaginating] = React.useState(false);
  const _prefSyncRef = React.useRef(null);

  // Social state lifted here so badges + deep links work from any tab
  const [friends, setFriends] = React.useState([]);
  const [plans, setPlans] = React.useState([]);
  const [shareInbox, setShareInbox] = React.useState([]);
  const [incomingCount, setIncomingCount] = React.useState(0);
  // Chat threads. null = not loaded (skeletons), [] = loaded and empty.
  const [conversations, setConversations] = React.useState(null);
  const [openChatId, setOpenChatId] = React.useState(null);
  // Activities of done entries, hydrated from the server — so the passport
  // renders on a fresh device even when the deck doesn't contain them.
  const [doneActivities, setDoneActivities] = React.useState([]);
  // True until the initial passport fetch resolves — lets the passport show
  // a loading skeleton instead of the "No stamps yet" empty state while a
  // cold Cloud Run instance or a slow query is still in flight.
  const [doneLoading, setDoneLoading] = React.useState(true);
  // Friends' recent done places, deck-compatible activities with `friendDid`
  // attribution — the data source for the friends card stack.
  // null = not loaded yet (feed shows skeletons), [] = loaded and empty.
  const [friendsActivity, setFriendsActivity] = React.useState(null);

  // home base — real GPS, same origin for every card/modal this render
  const _predefined = LOCATIONS.find(l => l.id === locationId) || LOCATIONS[0];
  const base = customGPS
    ? { name: customGPS.name || 'Custom location', lat: customGPS.lat, lon: customGPS.lon }
    : _predefined;

  const activities = apiActivities || [];

  React.useEffect(() => { setAnalyticsArea(base.lat, base.lon); }, [base.lat, base.lon]);

  // ── First-run location guess ──────────────────────────────────────────────
  // Every new visitor used to start in Times Square until they found the
  // location picker; someone in London got a New York deck. On a genuinely
  // fresh visit (nothing persisted yet), guess a coarse location from IP:
  // snap to the nearest preset city when close enough (that's where coverage
  // is curated), otherwise pin the raw coords. Never overrides a location the
  // user has touched.
  const _freshVisit = React.useMemo(() => {
    try { return !localStorage.getItem(stateKey(null)); } catch { return false; }
  }, []);
  const _locTouchedRef = React.useRef(false);
  const _geoGuessRef = React.useRef(null);
  const applyGeoGuess = (g) => {
    if (_locTouchedRef.current) return;
    const nearest = LOCATIONS.reduce((best, l) => {
      const d = haversineMiles(g.lat, g.lon, l.lat, l.lon);
      return d < best.d ? { l, d } : best;
    }, { l: null, d: Infinity });
    if (nearest.d <= 100) { setLocationId(nearest.l.id); setCustomGPS(null); }
    else setCustomGPS({ lat: g.lat, lon: g.lon, name: g.city || 'Near you' });
  };
  React.useEffect(() => {
    if (!_freshVisit) return;
    let alive = true;
    guessIPLocation().then(g => {
      if (!alive || !g) return;
      _geoGuessRef.current = g; // kept for re-applying after a fresh sign-in bucket swap
      applyGeoGuess(g);
    });
    return () => { alive = false; };
  }, []);

  // ── Nearby feed ────────────────────────────────────────────────────────────
  const _resettingRef = React.useRef(false);
  const _nearbyRef    = React.useRef(null);
  // Tracks pagination fetches that don't grow the visible deck, so a sparse/
  // out-of-range location can't make the load-more effect below spin forever.
  const _staleFetchesRef = React.useRef(0);
  const _lastDeckLenRef  = React.useRef(0);
  // Gated on nearbyRefreshKey rather than firing whenever locationId/prefs/
  // customGPS change — those all only change on the Vibe (settings) screen,
  // and eagerly re-fetching on every slider/distance/location tweak while
  // someone is still configuring things burns API calls for feeds they may
  // never see. "Find things to do" and Discover's "Retry" bump the key
  // explicitly; the effect below does too, the first time Discover is
  // reached without either of those (bottom-nav tap, deep link). Whichever
  // trips it first, the values captured here (locationId/prefs/customGPS/
  // vibe) are always whatever's currently set, since they're read fresh via
  // closure when the timeout fires.
  React.useEffect(() => {
    if (!authReady || nearbyRefreshKey === 0) return;
    _resettingRef.current = true;
    clearTimeout(_nearbyRef.current);
    setApiStatus('loading');
    setApiError(null);
    setPaginationToken(null);
    setPaginating(false);
    _staleFetchesRef.current = 0;
    _lastDeckLenRef.current = 0;
    _nearbyRef.current = setTimeout(() => {
      const gps = customGPS || LOCATIONS.find(l => l.id === locationId) || LOCATIONS[0];
      const coords = `${gps.lat},${gps.lon}`;
      // Signed out, the deck still works: the public feed serves the same
      // places (sanitized, default server prefs) so a visitor swipes before
      // ever being asked to sign in.
      const page = authUser
        ? apiFetchNearby(coords, Math.round(Math.max(prefs.distance, 2) * 1609), null, !prefs.showRevisits)
        : apiFetchPublicNearby(coords, Math.round(Math.max(prefs.distance, GUEST_DISTANCE_MI) * 1609));
      const toActivity = authUser ? placeToActivity : publicPlaceToActivity;
      const rankVibe = authUser ? vibe : GUEST_VIBE;
      page
        .then(res => {
          // Ranked once here, at fetch time, rather than in the deck memo —
          // sorting the whole list on every render would reshuffle cards the
          // user is mid-swipe on whenever a later page loads in (see below).
          const ranked = (res.locations || []).map(toActivity)
            .sort((a, b) => matchScore(rankVibe, b) - matchScore(rankVibe, a));
          setApiActivities(ranked);
          setDismissedIds([]);
          setPaginationToken(res.hasMore ? (res.paginationToken || null) : null);
          setApiStatus('idle');
        })
        .catch(err => { setApiStatus('error'); setApiError(err?.offline ? 'offline' : 'error'); })
        .finally(() => { _resettingRef.current = false; });
    }, 600);
  }, [authUser, authReady, nearbyRefreshKey]);

  // Reaching Discover without ever hitting "Find things to do" (bottom-nav
  // tap, deep link to /discover) needs its own trigger, or the tab would
  // just sit empty forever now that the feed doesn't load eagerly.
  React.useEffect(() => {
    if (tab === 'discover' && authReady && nearbyRefreshKey === 0) {
      setNearbyRefreshKey(k => k + 1);
    }
  }, [tab, authReady, nearbyRefreshKey]);

  // On first auth: load preferences from server
  React.useEffect(() => {
    if (!authUser) return;
    apiGetPreferences()
      .then(p => { if (p) setVibe(apiPrefsToVibe(p)); })
      .catch(() => {});
    // Push the Google identity up — this is where the server learns your
    // display name (shown to friends in chats, shares, and notifications).
    const fbUser = larkleGetUser();
    if (fbUser) {
      apiSyncProfile({
        ...(fbUser.displayName ? { name: fbUser.displayName } : {}),
        ...(fbUser.email ? { email: fbUser.email } : {}),
        ...(fbUser.photoURL ? { photoUrl: fbUser.photoURL } : {}),
      }).catch(() => {});
    }
  }, [authUser]);

  // Hydrate the done/passport list from the server (Liked views carry the
  // structured rating) — full replace, not a merge-by-id with whatever was
  // there before. Place ids are re-encrypted fresh on every response, so an
  // id persisted from an earlier load/session never matches this one; trying
  // to "keep" not-yet-matched local entries just piled up permanent
  // duplicates that could never resolve to a place again (see doneActivities
  // join below) and made this list grow without bound.
  React.useEffect(() => {
    if (!authUser) { setDoneEntries([]); setDoneActivities([]); return; }
    setDoneLoading(true);
    apiFetchMyViews('Liked', { limit: 100 })
      .then(entries => {
        const fromServer = (entries || []).map(({ view }) => viewToDoneEntry(view))
          .sort((a, b) => (b.whenTs || 0) - (a.whenTs || 0));
        setDoneEntries(fromServer);
        setDoneActivities((entries || [])
          .filter(e => e.place)
          .map(e => placeToActivity(e.place)));
      })
      .catch(() => {})
      .finally(() => setDoneLoading(false));
  }, [authUser]);

  // Saved fetch: re-runs when auth or any filter changes
  const _savedRef = React.useRef(null);
  const [savedRefreshKey, setSavedRefreshKey] = React.useState(0);
  React.useEffect(() => {
    if (!authUser) return;
    clearTimeout(_savedRef.current);
    setSavedLoading(true);
    setSavedError(null);
    setSavedActivities([]);
    setSavedHasMore(false);
    setSavedOffset(0);
    _savedRef.current = setTimeout(() => {
      const params = { limit: 25, offset: 0 };
      if (savedFilters.query)  params.queryString = savedFilters.query;
      if (savedFilters.coords) params.coords = savedFilters.coords;
      if (savedFilters.radius != null) params.radius = savedFilters.radius;
      apiFetchViewed('Saved', params)
        .then(res => {
          const saved = (res?.locations || []).map(placeToActivity);
          setSavedActivities(saved);
          setSavedHasMore(res?.hasMore ?? false);
          setSavedOffset(25);
        })
        .catch(err => setSavedError(err?.offline ? 'offline' : 'error'))
        .finally(() => setSavedLoading(false));
    }, savedFilters.query ? 400 : 0);
  }, [authUser, savedFilters, savedRefreshKey]);

  // Debounced preference sync
  React.useEffect(() => {
    if (!authUser) return;
    clearTimeout(_prefSyncRef.current);
    _prefSyncRef.current = setTimeout(() => {
      apiUpdatePreferences(vibeToApiPrefs(vibe)).catch(() => {});
    }, 1500);
  }, [authUser, vibe]);

  // ── Social polling: friends, requests, plans, share inbox ────────────────
  React.useEffect(() => {
    if (!authUser) {
      setFriends([]); setPlans([]); setShareInbox([]); setIncomingCount(0);
      setDoneActivities([]); setFriendsActivity(null); setConversations(null);
      return;
    }
    let alive = true;
    const poll = () => {
      apiFetchFriends().then(f => alive && setFriends(f || [])).catch(() => {});
      apiFetchIncomingRequests().then(r => alive && setIncomingCount((r || []).length)).catch(() => {});
      apiFetchShareInbox({ limit: 50, includeRead: true })
        .then(list => alive && setShareInbox((list || []).map(s => ({
          ...s, activity: s.place ? placeToActivity(s.place) : null,
        }))))
        .catch(() => {});
      apiFetchPlans()
        .then(p => alive && setPlans((p || []).filter(pl => pl.status !== 'Cancelled' && pl.status !== 'Completed')))
        .catch(() => {});
      apiFetchConversations()
        .then(list => alive && setConversations(list || []))
        .catch(() => { if (alive) setConversations(c => c ?? []); });
      apiFetchFriendsActivity(50)
        .then(list => alive && setFriendsActivity((list || [])
          .filter(e => e.place)
          .map(e => ({
            ...placeToActivity(e.place),
            friendDid: {
              userId: e.friend.userId,
              name: e.friend.name,
              handle: e.friend.handle,
              photoUrl: e.friend.photoUrl,
              whenTs: (e.view.viewTimeSec || 0) * 1000,
              stars: e.view.rating?.stars,
              note: e.view.rating?.note,
            },
          }))))
        .catch(() => { if (alive) setFriendsActivity(f => f ?? []); });
    };
    poll();
    const iv = setInterval(poll, 60_000);
    const onFocus = () => poll();
    window.addEventListener('focus', onFocus);
    return () => { alive = false; clearInterval(iv); window.removeEventListener('focus', onFocus); };
  }, [authUser]);

  // ── derived ────────────────────────────────────────────────────────────────
  // Filter only — no sort. Activities already arrive ranked (sorted once at
  // fetch time, see the nearby-feed effects below); re-sorting here on every
  // recompute would reshuffle the visible stack whenever a background
  // pagination fetch appends a highly-ranked card.
  //
  // Already-saved/seen/done places are NOT filtered out here — the server
  // already excludes them (LocationServiceImpl.getLocationsForUser, based on
  // real view history) before the feed is even returned, so re-checking
  // against locally-persisted id lists here would just be redundant *and*
  // wrong (place ids are re-encrypted fresh on every response, so a
  // locally-remembered id never matches a later fetch of the same place).
  const deck = React.useMemo(() => {
    const maxDist = authUser ? prefs.distance : Math.max(prefs.distance, GUEST_DISTANCE_MI);
    return activities
      .filter(a => !dismissedIds.includes(a.id))
      .filter(a => prefs.price === 0 ? true : a.price <= prefs.price)
      .filter(a => milesFrom(a, base) <= maxDist);
  }, [prefs, locationId, apiActivities, customGPS, dismissedIds, authUser]);

  // Impression = a card reaching the top of the deck while Discover is
  // visible. Deduped per session so tab-hopping or a prefs re-filter doesn't
  // recount a card the user already saw.
  const _impressedRef = React.useRef(new Set());
  const _topCard = tab === 'discover' ? deck[0] : null;
  React.useEffect(() => {
    if (!_topCard || _impressedRef.current.has(_topCard.id)) return;
    _impressedRef.current.add(_topCard.id);
    trackEvent('card_impression', { place_id: _topCard.id, place_key: placeKey(_topCard) });
  }, [_topCard?.id]);

  const myUid = larkleGetUid();
  const pendingRsvps = plans.filter(p =>
    (p.status === 'Open' || p.status === 'Finalized') &&
    (p.invitees || []).some(inv => inv.userId === myUid && inv.rsvp === 'Pending')
  ).length;
  const unreadShares = shareInbox.filter(s => s.readAt == null).length;
  const unreadChats = (conversations || []).filter(c => c.unread).length;
  const friendsBadge = incomingCount + pendingRsvps + unreadShares + unreadChats;

  const loadMoreSaved = React.useCallback(() => {
    if (!authUser || !savedHasMore || savedPaginating) return;
    setSavedPaginating(true);
    const params = { limit: 25, offset: savedOffset };
    if (savedFilters.query)  params.queryString = savedFilters.query;
    if (savedFilters.coords) params.coords = savedFilters.coords;
    if (savedFilters.radius != null) params.radius = savedFilters.radius;
    apiFetchViewed('Saved', params)
      .then(res => {
        const more = (res?.locations || []).map(placeToActivity);
        setSavedActivities(prev => {
          const seen = new Set(prev.map(a => a.id));
          return [...prev, ...more.filter(a => !seen.has(a.id))];
        });
        setSavedOffset(o => o + 25);
        setSavedHasMore(res?.hasMore ?? false);
      })
      .catch(() => {})
      .finally(() => setSavedPaginating(false));
  }, [authUser, savedHasMore, savedPaginating, savedOffset, savedFilters]);

  // Fetch next page when the deck runs low. If several pages in a row don't
  // actually grow the deck (e.g. a location with nothing left within
  // prefs.distance), stop instead of refetching indefinitely.
  const MAX_STALE_FETCHES = 4;
  React.useEffect(() => {
    if (!authReady || !paginationToken || paginating || apiStatus !== 'idle') return;
    if (_resettingRef.current) return;
    if (deck.length > 5) return;

    if (deck.length === _lastDeckLenRef.current) {
      _staleFetchesRef.current += 1;
    } else {
      _staleFetchesRef.current = 0;
      _lastDeckLenRef.current = deck.length;
    }
    if (_staleFetchesRef.current >= MAX_STALE_FETCHES) { setPaginationToken(null); return; }

    setPaginating(true);
    const gps = customGPS || LOCATIONS.find(l => l.id === locationId) || LOCATIONS[0];
    const coords = `${gps.lat},${gps.lon}`;
    const page = authUser
      ? apiFetchNearby(coords, Math.round(Math.max(prefs.distance, 2) * 1609), paginationToken, !prefs.showRevisits)
      : apiFetchPublicNearby(coords, Math.round(Math.max(prefs.distance, GUEST_DISTANCE_MI) * 1609), paginationToken);
    const toActivity = authUser ? placeToActivity : publicPlaceToActivity;
    const rankVibe = authUser ? vibe : GUEST_VIBE;
    page
      .then(res => {
        // Rank the new page among itself and append after the existing,
        // already-ranked cards — never re-sort the combined list, or a
        // strong match on this page would jump ahead of the visible stack.
        const incoming = (res.locations || []).map(toActivity)
          .sort((a, b) => matchScore(rankVibe, b) - matchScore(rankVibe, a));
        setApiActivities(prev => {
          const seen = new Set((prev || []).map(a => a.id));
          return [...(prev || []), ...incoming.filter(a => !seen.has(a.id))];
        });
        setPaginationToken(res.hasMore ? (res.paginationToken || null) : null);
        setPaginating(false);
      })
      .catch(() => setPaginating(false));
  }, [authUser, authReady, deck.length, paginationToken, paginating, apiStatus, locationId, prefs.distance, prefs.showRevisits, customGPS]);

  // ── deep links ─────────────────────────────────────────────────────────────
  // /a/:placeId — open the place (fetch it if it isn't in memory)
  React.useEffect(() => {
    const m = path.match(/^\/a\/([^/]+)$/);
    if (!m) { setFetchedDetail(null); return; }
    const id = decodeURIComponent(m[1]);
    setDetailId(id);
    const known = activities.find(a => a.id === id) || savedActivities.find(a => a.id === id);
    if (known || !authUser) return;
    let alive = true;
    apiFetchLocation(id)
      .then(p => {
        // The server re-encrypts place ids with a fresh IV on every response
        // (see PlaceIdCipherService), so p.id here won't match the URL's id
        // even though it's the same place. Pin it back to `id` — that's what
        // detailId/findActivity key on, and it still decrypts to the same
        // real place server-side for any later markViewed/save/share call.
        if (alive && p) setFetchedDetail({ ...placeToActivity(p), id });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [path, authUser]);

  // "Save it" funnel from the public share page and the guest deck gate:
  // once signed in, complete the save the visitor asked for. The activity may
  // no longer be in memory (the iOS redirect sign-in reloads the page), so
  // fall back to fetching by id — a previously-issued encrypted id still
  // decrypts to the same place server-side.
  const _pendingSaveRef = React.useRef(false);
  React.useEffect(() => {
    if (!authUser) return;
    let pendingId = null;
    try { pendingId = sessionStorage.getItem('larkle-pending-save'); } catch {}
    if (!pendingId || _pendingSaveRef.current) return;
    const finish = (a) => {
      _pendingSaveRef.current = false;
      try { sessionStorage.removeItem('larkle-pending-save'); } catch {}
      if (!a) return;
      setSavedActivities(s => s.some(x => x.id === a.id) ? s : [a, ...s]);
      markViewedSafe(a, 'Saved');
      trackEvent('save_place', { place_id: a.id, place_key: placeKey(a), source: 'pending_save' });
      setToast('Saved to your list');
    };
    const known = findActivity(pendingId);
    if (known) { finish(known); return; }
    _pendingSaveRef.current = true;
    apiFetchLocation(pendingId)
      .then(p => finish(p ? { ...placeToActivity(p), id: pendingId } : null))
      .catch(() => {
        _pendingSaveRef.current = false;
        try { sessionStorage.removeItem('larkle-pending-save'); } catch {}
      });
  }, [authUser, fetchedDetail, apiActivities]);

  // /c/:conversationId — open the chat thread. Like /p/, this only opens on
  // match (never self-clears): closing a place card opened from the thread
  // rewrites the URL, and the thread must survive that.
  React.useEffect(() => {
    const m = path.match(/^\/c\/([^/]+)$/);
    if (!m || !authUser) return;
    setOpenChatId(decodeURIComponent(m[1]));
  }, [path, authUser]);

  // /p/:planId — open the plan detail
  React.useEffect(() => {
    const m = path.match(/^\/p\/([^/]+)$/);
    if (!m || !authUser) return;
    const id = decodeURIComponent(m[1]);
    const known = plans.find(p => p.id === id);
    if (known) { setOpenPlan(known); return; }
    let alive = true;
    apiGetPlan(id)
      .then(p => { if (alive && p) setOpenPlan(p); })
      .catch(() => { setToast('Could not open that plan'); navigate('/friends'); });
    return () => { alive = false; };
  }, [path, authUser]);

  // ── actions ────────────────────────────────────────────────────────────────
  const markViewedSafe = (a, status) => {
    if (authUser && a?._place) apiMarkViewed([placeToLocationView(a, status)]).catch(() => {});
  };

  // The guest conversion moment: sign-in is asked for at the point of desire
  // (keeping a place they just swiped on), not at the front door.
  const openGuestGate = (activity, reason) => {
    setGuestGate({ activity: activity || null, reason });
    trackEvent('guest_gate_view', { reason, ...(activity ? { place_key: placeKey(activity) } : {}) });
  };
  const onGateSignIn = () => {
    const a = guestGate?.activity;
    // Stash the save-intent so it completes after auth — sessionStorage
    // rather than memory because the iOS redirect fallback reloads the page.
    if (a && guestGate.reason === 'save') {
      try { sessionStorage.setItem('larkle-pending-save', a.id); } catch {}
    }
    trackEvent('sign_in_clicked', { source: 'guest_gate', reason: guestGate?.reason });
    larkleSignIn().catch(err => setToast(signInErrorMessage(err)));
  };
  React.useEffect(() => { if (authUser) setGuestGate(null); }, [authUser]);

  const onSwipe = (id, dir) => {
    const a = activities.find(x => x.id === id);
    if (!authUser && dir === 'right') {
      // Don't dismiss: the card slides back under the gate sheet, and if the
      // visitor signs in the pending save completes against this same place.
      trackEvent('deck_swipe', { direction: 'like', place_id: id, place_key: placeKey(a), guest: true });
      openGuestGate(a, 'save');
      return;
    }
    setDismissedIds(d => d.includes(id) ? d : [id, ...d]);
    trackEvent('deck_swipe', { direction: dir === 'right' ? 'like' : 'pass', place_id: id, place_key: placeKey(a) });
    if (dir === 'right') {
      if (a) setSavedActivities(s => s.some(x => x.id === id) ? s : [a, ...s]);
      setToast('Saved');
      markViewedSafe(a, 'Saved');
    } else {
      markViewedSafe(a, 'Disliked');
    }
  };

  const findActivity = (id) =>
    activities.find(a => a.id === id)
    || savedActivities.find(a => a.id === id)
    || doneActivities.find(a => a.id === id)
    || (friendsActivity || []).find(a => a.id === id)
    || shareInbox.find(s => s.activity?.id === id)?.activity
    || (fetchedDetail?.id === id ? fetchedDetail : null);

  const onSave = (id) => {
    const a = findActivity(id);
    if (savedActivities.some(x => x.id === id)) {
      setSavedActivities(s => s.filter(x => x.id !== id));
      markViewedSafe(a, 'Disliked');
      trackEvent('unsave_place', { place_id: id, place_key: placeKey(a) });
    } else {
      if (a) setSavedActivities(s => s.some(x => x.id === id) ? s : [a, ...s]);
      setToast('Saved');
      markViewedSafe(a, 'Saved');
      trackEvent('save_place', { place_id: id, place_key: placeKey(a) });
    }
  };

  const onRemoveSaved = (id) => {
    const a = savedActivities.find(x => x.id === id);
    setSavedActivities(s => s.filter(x => x.id !== id));
    markViewedSafe(a, 'Disliked');
  };

  const onMarkDone = (id) => {
    setRateId(id);
    setDetailId(null);
  };

  const onSaveRating = (id, payload) => {
    const now = new Date();
    const today = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    setDoneEntries(d => {
      const filtered = d.filter(e => e.id !== id);
      return [{ id, when: today, whenTs: now.getTime(), ...payload }, ...filtered];
    });
    setSavedActivities(s => s.filter(a => a.id !== id));
    setRateId(null);
    setToast('Stamped ✓ in your passport');
    const a = findActivity(id);
    trackEvent('mark_done', { place_id: id, stars: payload.stars, place_key: placeKey(a) });
    if (a) setDoneActivities(s => s.some(x => x.id === id) ? s : [a, ...s]);
    if (authUser && a?._place) {
      // The structured rating rides along with the Liked view — this is what
      // /feed/views and /feed/friendsActivity read back.
      const rating = {
        stars: payload.stars,
        vibe:  payload.axes?.vibe  ?? 0,
        value: payload.axes?.value ?? 0,
        again: payload.axes?.again ?? 0,
        ...(payload.note ? { note: payload.note } : {}),
      };
      apiMarkViewed([placeToLocationView(a, 'Liked', rating)])
        .catch(err => setToast(err?.offline ? "Saved locally, won't sync until online" : 'Stamped, but sync failed'));
      // Notes also feed the shared place descriptions (placeInfo)
      if (payload.note) {
        const meta = `${payload.stars}★ · Vibe: ${payload.axes?.vibe}/5 · Value: ${payload.axes?.value}/5 · Again: ${payload.axes?.again}/5`;
        apiAddReview(id, `${payload.note}\n\n${meta}`).catch(() => {});
      }
    }
  };

  // The server mirrors shares and plans into chat threads — pull the list
  // right away instead of waiting for the next poll tick.
  const refreshConversations = () => {
    apiFetchConversations().then(list => setConversations(list || [])).catch(() => {});
  };

  // Share a place to friends — the real thing (POST /share)
  const onShareToFriend = (id, friendIds, note) => {
    return apiSendShare(friendIds, id, note)
      .then(() => {
        setShareId(null);
        const names = friendIds
          .map(fid => friends.find(f => f.userId === fid)?.name?.split(' ')[0])
          .filter(Boolean);
        setToast(names.length ? `Sent to ${names.join(', ')} — it's in your chat too` : 'Sent');
        trackEvent('share_place', { place_id: id, recipient_count: friendIds.length, place_key: placeKey(findActivity(id)) });
        refreshConversations();
      })
      .catch(err => setToast(err?.offline ? "You're offline" : 'Could not send'));
  };

  // Make a plan with this place attached — one call, plan starts Finalized
  const onMakePlan = (id, friendIds, whenTs, title) => {
    return apiCreatePlan({
      title,
      inviteeIds: friendIds,
      finalPlaceId: id,
      ...(whenTs ? { scheduledFor: whenTs } : {}),
    })
      .then(plan => {
        setShareId(null);
        trackEvent('plan_created', { source: 'share_sheet', invitee_count: friendIds.length, has_place: true });
        setPlans(p => [plan, ...p.filter(x => x.id !== plan.id)]);
        setToast('Plan created');
        refreshConversations();
        navigate(`/p/${plan.id}`);
      })
      .catch(err => setToast(err?.offline ? "You're offline" : 'Could not create plan'));
  };

  const onDeleteAccount = () => {
    return apiDeleteAccount()
      .then(() => {
        try { localStorage.removeItem(stateKey(uid)); } catch {}
        setToast('Account deleted');
        return larkleSignOut().catch(() => {});
      })
      .then(() => { setAuthUser(null); navigate('/'); })
      .catch(() => setToast('Could not delete account'));
  };

  const onSignOut = async () => {
    try { localStorage.removeItem(stateKey(uid)); } catch {}
    await disablePush().catch(() => {}); // needs auth — before signing out
    larkleSignOut();
    setApiActivities(null);
    setSavedActivities([]);
    setSavedHasMore(false);
    setSavedOffset(0);
    setSavedFilters({ query: '', coords: null, radius: null });
    setAuthUser(null);
    navigate('/');
    setToast('Signed out');
  };

  const detail = detailId ? findActivity(detailId) : null;
  const shareActivity = shareId ? findActivity(shareId) : null;
  const rateActivity = rateId ? findActivity(rateId) : null;
  const doneEntryFor = (id) => doneEntries.find(e => e.id === id);

  const closeOverlayRoute = () => {
    if (/^\/(a|p|c)\//.test(path)) navigate(TAB_PATHS[tab] || '/');
  };

  // Phone/browser back button should close the place detail modal instead of
  // leaving the screen behind it — push a no-op history entry while it's
  // open (whatever triggered the open: card tap, deep link, share inbox…)
  // and treat popping it as a close. Doesn't touch the URL, so it's inert
  // for every other flow.
  React.useEffect(() => {
    if (detailId) trackEvent('place_detail_view', { place_id: detailId, place_key: placeKey(findActivity(detailId)) });
  }, [detailId]);

  const detailPushedRef = React.useRef(false);
  React.useEffect(() => {
    if (!detailId) return;
    window.history.pushState({ larkleDetail: true }, '');
    detailPushedRef.current = true;
    const onPopState = () => { detailPushedRef.current = false; setDetailId(null); };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [detailId]);

  // Explicit close (X / share / plan-it): consume the entry we pushed above,
  // unless the URL itself is the overlay route (a genuine /a/:id deep link) —
  // closeOverlayRoute() already escapes that case with its own navigation,
  // and firing both at once races two history operations against each other.
  const closeDetail = () => {
    if (detailPushedRef.current && !/^\/a\//.test(path)) {
      detailPushedRef.current = false;
      window.history.back();
    }
    setDetailId(null);
    setFetchedDetail(null);
  };

  // A chat started from the Friends tab (DM button or new-chat panel):
  // register it and jump into the thread.
  const onChatStarted = (convo) => {
    setConversations(list => [convo, ...(list || []).filter(c => c.id !== convo.id)]);
    navigate(`/c/${convo.id}`);
  };

  // Signed-out public place page for shared links
  if (!authUser && /^\/a\//.test(path)) {
    return <PublicPlacePage placeId={decodeURIComponent(path.split('/')[2])} onToast={setToast} toast={toast} clearToast={() => setToast('')}/>;
  }
  // Invite redemption
  if (/^\/invite\//.test(path)) {
    return <InvitePage code={decodeURIComponent(path.split('/')[2])} authUser={authUser}
                       onDone={() => navigate('/friends')} toast={toast} onToast={setToast} clearToast={() => setToast('')}/>;
  }
  // Session still resolving — brand splash, no login flash
  if (!authReady) {
    return <SplashScreen/>;
  }
  // Signed out on /discover — the guest deck. The full swipe experience,
  // fed by the public endpoint; sign-in is asked for on save/share (the
  // GuestGateSheet), not up front.
  if (!authUser && tab === 'discover') {
    return (
      <HomeBaseContext.Provider value={base}>
      <div className="app" data-screen-label="guest-discover">
        <div className="app-stage">
          <DiscoverScreen vibe={GUEST_VIBE} deck={deck} locationName={base.name}
                          onSwipe={onSwipe}
                          onOpen={(id) => setDetailId(id)}
                          onShare={(id) => openGuestGate(findActivity(id), 'share')}
                          onTweakVibe={() => openGuestGate(null, 'vibe')}
                          loading={apiStatus === 'loading' || paginating}
                          error={apiError}
                          onRetry={() => setNearbyRefreshKey(k => k + 1)}/>
          <button className="chip" style={{
            position: 'absolute', top: 16, right: 18, zIndex: 20,
            height: 30, padding: '0 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
            background: 'var(--paper)', boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
          }} onClick={() => {
            trackEvent('sign_in_clicked', { source: 'guest_header' });
            larkleSignIn().catch(err => setToast(signInErrorMessage(err)));
          }}>
            Sign in
          </button>
        </div>
        {detail && (
          <DetailModal activity={detail} vibe={GUEST_VIBE}
                       isSaved={false}
                       onClose={() => { closeDetail(); closeOverlayRoute(); }}
                       onSave={(id) => { closeDetail(); openGuestGate(findActivity(id), 'save'); }}
                       onShare={(id) => { closeDetail(); openGuestGate(findActivity(id), 'share'); }}
                       onPlanIt={(id) => { closeDetail(); openGuestGate(findActivity(id), 'share'); }}
                       onMarkDone={(id) => { closeDetail(); openGuestGate(findActivity(id), 'save'); }}/>
        )}
        {guestGate && (
          <GuestGateSheet gate={guestGate}
                          onClose={() => setGuestGate(null)}
                          onSignIn={onGateSignIn}/>
        )}
        <Toast msg={toast} onDone={() => setToast('')}/>
      </div>
      </HomeBaseContext.Provider>
    );
  }
  // Signed out — the welcome screen is the front door
  if (!authUser) {
    return <WelcomeScreen
             onExplore={() => navigate('/discover')}
             onSignIn={() => larkleSignIn().catch(err => setToast(signInErrorMessage(err)))}
             toast={toast} clearToast={() => setToast('')}/>;
  }

  const screen = (() => {
    switch (tab) {
      case 'vibe':
        return <VibeScreen vibe={vibe} setVibe={setVibe} prefs={prefs} setPrefs={setPrefs}
                           base={base} locationId={locationId}
                           activities={activities}
                           customGPS={customGPS}
                           onMapClick={(lat, lon) => { _locTouchedRef.current = true; setCustomGPS({ lat, lon }); }}
                           onClearCustom={() => setCustomGPS(null)}
                           onChangeLocation={() => setLocPickerOpen(true)}
                           onStart={() => {
                             trackEvent('find_things_tap');
                             setTab('discover');
                             if (authUser) {
                               clearTimeout(_prefSyncRef.current);
                               setApiActivities(null);
                               // Save preferences first, then fetch so the backend
                               // ranks places against the updated axes.
                               apiUpdatePreferences(vibeToApiPrefs(vibe))
                                 .finally(() => setNearbyRefreshKey(k => k + 1));
                             }
                           }}/>;
      case 'discover':
        return <DiscoverScreen vibe={vibe} deck={deck} locationName={base.name}
                               onSwipe={onSwipe}
                               onOpen={(id) => setDetailId(id)}
                               onShare={(id) => setShareId(id)}
                               onTweakVibe={() => setTab('vibe')}
                               loading={apiStatus === 'loading' || paginating}
                               error={apiError}
                               onRetry={() => setNearbyRefreshKey(k => k + 1)}/>;
      case 'saved':
        return <SavedScreen segment={path.startsWith('/done') ? 'been' : 'maybe'}
                            onSegmentChange={s => navigate(s === 'been' ? '/done' : '/saved')}
                            doneEntries={doneEntries}
                            doneActivities={[...doneActivities, ...activities, ...savedActivities]}
                            doneLoading={doneLoading}
                            onOpenEntry={(id) => setDetailId(id)}
                            savedActivities={savedActivities}
                            loading={savedLoading}
                            error={savedError}
                            onRetry={() => setSavedRefreshKey(k => k + 1)}
                            filters={savedFilters}
                            onQueryChange={q => setSavedFilters(f => ({ ...f, query: q }))}
                            onToggleNearMe={() => setSavedFilters(f => {
                              if (f.coords) return { ...f, coords: null, radius: null };
                              const gps = customGPS || LOCATIONS.find(l => l.id === locationId) || LOCATIONS[0];
                              return { ...f, coords: `${gps.lat},${gps.lon}`, radius: Math.round(Math.max(prefs.distance, 2) * 1609) };
                            })}
                            onOpen={(id) => setDetailId(id)}
                            onMarkDone={onMarkDone}
                            onShare={(id) => setShareId(id)}
                            onRemove={onRemoveSaved}
                            onLoadMore={savedHasMore ? loadMoreSaved : null}
                            loadingMore={savedPaginating}/>;
      case 'friends':
        return <FriendsScreen authUser={authUser}
                              activities={activities}
                              friendsActivity={friendsActivity}
                              savedActivities={savedActivities}
                              onSaveActivity={onSave}
                              plans={plans} setPlans={setPlans}
                              shareInbox={shareInbox}
                              conversations={conversations}
                              onOpenChat={(id) => navigate(`/c/${id}`)}
                              onChatStarted={onChatStarted}
                              onOpenShareInbox={() => setInboxOpen(true)}
                              onOpenActivity={(idOrActivity) => {
                                if (typeof idOrActivity === 'string') { setDetailId(idOrActivity); return; }
                                setFetchedDetail(idOrActivity);
                                setDetailId(idOrActivity.id);
                              }}
                              onOpenPlan={(id) => navigate(`/p/${id}`)}
                              onToast={setToast}
                              onGoToProfile={() => setTab('profile')}/>;
      case 'profile':
        return <ProfileScreen
                 authUser={authUser}
                 doneEntries={doneEntries}
                 savedActivities={savedActivities}
                 vibe={vibe}
                 onSignIn={() => larkleSignIn().catch(err => setToast(signInErrorMessage(err)))}
                 onSignOut={onSignOut}
                 onDeleteAccount={onDeleteAccount}
                 onToast={setToast}
                 onRefreshConversations={refreshConversations}/>;
      default: return null;
    }
  })();

  return (
    <HomeBaseContext.Provider value={base}>
      <div className="app" data-screen-label={`tab-${tab}`}>
        <div className="app-stage">
          {screen}
        </div>
        <BottomTabs current={tab} onChange={setTab} badges={{ friends: friendsBadge }}/>

        <Toast msg={toast} onDone={() => setToast('')}/>

        {/* Before DetailModal in the DOM so an opened place card stacks above the thread */}
        {openChatId && (
          <ChatThreadSheet conversationId={openChatId}
                           onClose={() => { setOpenChatId(null); closeOverlayRoute(); }}
                           onOpenPlace={(activity) => { setFetchedDetail(activity); setDetailId(activity.id); }}
                           onRead={(id) => setConversations(list => (list || []).map(c => c.id === id ? { ...c, unread: false } : c))}
                           onToast={setToast}/>
        )}

        {detail && !rateActivity && (
          <DetailModal activity={detail} vibe={vibe}
                       isSaved={detail.viewStatus === 'Saved' || savedActivities.some(x => x.id === detail.id)}
                       doneEntry={doneEntryFor(detail.id)}
                       onClose={() => { closeDetail(); closeOverlayRoute(); }}
                       onSave={onSave}
                       onShare={(id) => { closeDetail(); setShareMode('quick'); setShareId(id); closeOverlayRoute(); }}
                       onPlanIt={(id) => { closeDetail(); setShareMode('plan'); setShareId(id); closeOverlayRoute(); }}
                       onMarkDone={onMarkDone}/>
        )}
        {shareActivity && (
          <ShareSheet activity={shareActivity} friends={friends}
                      initialMode={shareMode}
                      onClose={() => { setShareId(null); setShareMode('quick'); }}
                      onShareToFriend={onShareToFriend}
                      onMakePlan={onMakePlan}
                      onToast={setToast}/>
        )}
        {rateActivity && (
          <RateSheet activity={rateActivity} existing={doneEntryFor(rateActivity.id)}
                     onClose={() => setRateId(null)}
                     onSave={onSaveRating}/>
        )}
        {openPlan && (
          <PlanDetailSheet plan={openPlan}
                           friends={friends}
                           savedActivities={savedActivities}
                           activities={activities}
                           onClose={() => { setOpenPlan(null); closeOverlayRoute(); }}
                           onOpenActivity={(id) => setDetailId(id)}
                           onPlanChange={p => { setOpenPlan(p); setPlans(list => list.map(x => x.id === p.id ? p : x)); }}
                           onPlanGone={id => { setOpenPlan(null); setPlans(list => list.filter(x => x.id !== id)); closeOverlayRoute(); }}
                           onToast={setToast}/>
        )}
        {inboxOpen && (
          <ShareInboxSheet shares={shareInbox}
                           onClose={() => setInboxOpen(false)}
                           onOpenActivity={(id) => { setInboxOpen(false); setDetailId(id); }}
                           onSaveShare={(activity) => { onSave(activity.id); }}
                           onMarkedRead={(ids) => setShareInbox(list => list.map(s => ids.includes(s.id) ? { ...s, readAt: Date.now() } : s))}
                           onToast={setToast}/>
        )}
        {locPickerOpen && (
          <LocationPicker locationId={locationId}
                          onClose={() => setLocPickerOpen(false)}
                          onPick={(id) => {
                            _locTouchedRef.current = true;
                            setLocationId(id);
                            setCustomGPS(null);
                            setLocPickerOpen(false);
                            setToast(`Now in ${LOCATIONS.find(l => l.id === id)?.name}`);
                          }}
                          onPickGPS={(lat, lon) => {
                            _locTouchedRef.current = true;
                            setCustomGPS({ lat, lon });
                            setLocPickerOpen(false);
                            setToast('Using your location');
                          }}/>
        )}
      </div>
    </HomeBaseContext.Provider>
  );
}

// ── Splash — shown only while Firebase resolves the session ─────────────────
function SplashScreen() {
  return (
    <div className="app">
      <div className="app-stage">
        <div className="screen" style={{
          background: 'var(--bg)', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 14,
        }}>
          <LarkleMark size={44}/>
          <span className="serif" style={{ fontSize: 38, color: 'var(--terracotta)' }}>larkle.</span>
        </div>
      </div>
    </div>
  );
}

// ── Welcome — the front door when signed out ────────────────────────────────
// A fan of postcards (the app's own generative art), the wordmark, and two
// ways in: straight into the guest deck (primary — show the product before
// asking for anything), or Google sign-in for returning users.
const WELCOME_CARDS = [
  { id: 'welcome-a', hue: 90,  art: 'forest-path' },
  { id: 'welcome-b', hue: 18,  art: 'sunset-bridge' },
  { id: 'welcome-c', hue: 350, art: 'pub-glow' },
];

function WelcomeScreen({ onExplore, onSignIn, toast, clearToast }) {
  React.useEffect(() => { trackEvent('welcome_view'); }, []);
  return (
    <div className="app">
      <div className="app-stage">
        <div className="screen" style={{
          background: 'linear-gradient(180deg, var(--bg-tint) 0%, var(--bg) 60%, var(--sky) 130%)',
        }}>
          <div className="scroll" style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '48px 28px', textAlign: 'center',
          }}>
            {/* postcard fan */}
            <div style={{ position: 'relative', width: 250, height: 208, marginBottom: 30 }} aria-hidden>
              {WELCOME_CARDS.map((c, i) => (
                <div key={c.id} style={{
                  position: 'absolute', left: '50%', top: 10,
                  width: 132, height: 174,
                  transform: `translateX(-50%) rotate(${(i - 1) * 11}deg) translateY(${i === 1 ? -8 : 4}px)`,
                  transformOrigin: '50% 130%',
                  background: 'var(--paper)', borderRadius: 14, padding: 7,
                  boxShadow: '0 10px 26px rgba(42,26,26,0.16)',
                  animation: `stamp-in 0.5s ${0.1 + i * 0.09}s cubic-bezier(.2,.8,.3,1) backwards`,
                }}>
                  <HeroArt activity={{ id: c.id, hue: c.hue, art: c.art, name: '' }} height={160} rounded={9} showNav={false}/>
                </div>
              ))}
              <div style={{
                position: 'absolute', right: 6, top: -6,
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '4px 11px', borderRadius: 8,
                border: '2px solid var(--terracotta)', color: 'var(--terracotta)',
                fontWeight: 700, fontSize: 10, letterSpacing: '0.14em',
                transform: 'rotate(8deg)', background: 'rgba(255,251,244,0.9)',
                fontFamily: 'JetBrains Mono, monospace',
                animation: 'stamp-in 0.4s 0.42s cubic-bezier(.2,.8,.3,1) backwards',
              }}><LarkleMark size={12}/>LARKLE</div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <LarkleMark size={38}/>
              <h1 className="serif" style={{ fontSize: 46, lineHeight: 1, margin: 0, color: 'var(--ink)', fontWeight: 400 }}>
                larkle<span style={{ color: 'var(--coral)' }}>.</span>
              </h1>
            </div>
            <p className="soft" style={{ fontSize: 15.5, lineHeight: 1.45, maxWidth: 270, margin: '0 0 28px' }}>
              Find the thing to do, and the people to do it with.
            </p>

            <button className="btn coral" style={{ height: 50, padding: '0 34px', fontSize: 15.5 }}
                    onClick={() => { trackEvent('explore_clicked'); onExplore(); }}>
              Start exploring
            </button>
            <button className="btn ghost" style={{ height: 44, padding: '0 26px', fontSize: 14, marginTop: 10 }}
                    onClick={() => { trackEvent('sign_in_clicked', { source: 'welcome' }); onSignIn(); }}>
              Continue with Google
            </button>
            <p style={{ fontSize: 12, color: 'var(--ink-mute)', marginTop: 16, maxWidth: 260, lineHeight: 1.5 }}>
              No account needed to look around. Sign in when you find something worth keeping.
            </p>
          </div>
        </div>
      </div>
      <Toast msg={toast} onDone={clearToast}/>
    </div>
  );
}

// ── Guest gate — the sign-in ask, timed to the moment of intent ─────────────
// Shown when a signed-out visitor tries to keep, share, or tune something.
// The place they wanted stays visible in the sheet so the value of signing in
// is concrete, not abstract.
function GuestGateSheet({ gate, onClose, onSignIn }) {
  const a = gate.activity;
  const headline =
    gate.reason === 'save'  ? 'Keep this one?' :
    gate.reason === 'share' ? 'Share it with a friend?' :
    'Make it yours';
  const sub =
    gate.reason === 'save'  ? 'Sign in and it lands on your maybe-list, synced on every device.' :
    gate.reason === 'share' ? 'Sign in to send places to friends and make plans together.' :
    'Sign in to tune your vibe, pick your spot, and keep what you find.';
  return (
    <>
      <div className="scrim" onClick={onClose}/>
      <div className="sheet" role="dialog" aria-label="Sign in to continue">
        <div className="handle"/>
        {a && (
          <div style={{
            display: 'flex', gap: 12, padding: 10, borderRadius: 16,
            background: 'var(--bg-tint)', marginBottom: 14,
            border: '0.5px solid var(--hair)',
          }}>
            <div style={{ width: 64, flexShrink: 0 }}>
              <HeroArt activity={a} height={64} rounded={10} showNav={false}/>
            </div>
            <div className="col" style={{ justifyContent: 'center', minWidth: 0 }}>
              <span className="serif" style={{ fontSize: 17, color: 'var(--ink)', lineHeight: 1.1, textWrap: 'pretty' }}>{a.name}</span>
              <span className="muted" style={{ fontSize: 11.5 }}>{a.kind}</span>
            </div>
          </div>
        )}
        <h3 className="serif" style={{ fontSize: 26, margin: '0 0 6px', color: 'var(--ink)', fontWeight: 400 }}>{headline}</h3>
        <p className="soft" style={{ fontSize: 14, lineHeight: 1.45, margin: '0 0 18px' }}>{sub}</p>
        <button className="btn coral full" style={{ height: 48 }} onClick={onSignIn}>
          Continue with Google
        </button>
        <button className="btn ghost full" style={{ height: 42, marginTop: 8 }} onClick={onClose}>
          Not now
        </button>
      </div>
    </>
  );
}

// ── Public (signed-out) place page for shared links ─────────────────────────
function PublicPlacePage({ placeId, toast, onToast, clearToast }) {
  const [activity, setActivity] = React.useState(null);
  const [status, setStatus] = React.useState('loading');
  // Un-enriched places carry their name as the description — same as the
  // detail modal, fall back to the review-derived copy in that case.
  const [reviewBlurb, setReviewBlurb] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    setStatus('loading');
    apiFetchPublicLocation(placeId)
      .then(p => { if (alive) { setActivity(publicPlaceToActivity(p)); setStatus('ok'); } })
      .catch(err => { if (alive) setStatus(err?.status === 404 ? 'notfound' : 'error'); });
    return () => { alive = false; };
  }, [placeId]);

  React.useEffect(() => {
    if (!activity) return;
    if (activity.blurb && activity.blurb.trim() !== activity.name.trim()) return;
    let alive = true;
    apiFetchPlaceReviews(placeId)
      .then(reviews => {
        const first = Array.isArray(reviews) ? reviews.find(r => r.reviewText) : null;
        if (alive && first) setReviewBlurb(first.reviewText);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [activity?.name]);

  const signInToSave = () => {
    try { sessionStorage.setItem('larkle-pending-save', placeId); } catch {}
    trackEvent('sign_in_clicked', { source: 'public_place' });
    larkleSignIn().catch(err => onToast(signInErrorMessage(err)));
  };

  return (
    <div className="app">
      <div className="app-stage">
        <div className="screen" style={{ background: 'var(--bg)' }}>
          {status === 'loading' && (
            <div className="scroll" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span className="serif" style={{ fontSize: 22, color: 'var(--ink-mute)' }}>Loading…</span>
            </div>
          )}
          {(status === 'error' || status === 'notfound') && (
            <div className="scroll" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, padding: 32, textAlign: 'center' }}>
              <div className="serif" style={{ fontSize: 30, color: 'var(--terracotta)' }}>
                {status === 'notfound' ? "That place isn't here anymore." : "Couldn't load this place."}
              </div>
              <a href="/" style={{ color: 'var(--coral)', fontSize: 14 }}>Open the app →</a>
            </div>
          )}
          {status === 'ok' && activity && (
            <div className="scroll" style={{ padding: '0 0 32px' }}>
              <div style={{ position: 'relative', height: 300 }}>
                <PublicHero activity={activity}/>
                <div style={{
                  position: 'absolute', left: 0, right: 0, bottom: 0,
                  padding: '40px 22px 18px',
                  background: 'linear-gradient(to top, rgba(20,10,8,0.9) 0%, transparent 100%)',
                  color: 'var(--paper)',
                }}>
                  <div className="chip dark" style={{ height: 22, fontSize: 10.5, marginBottom: 8 }}>{activity.kind}</div>
                  <h1 className="serif" style={{ fontSize: 30, lineHeight: 1.05, margin: 0, color: 'var(--paper)', fontWeight: 400 }}>
                    {activity.name}
                  </h1>
                </div>
              </div>
              <div style={{ padding: '18px 22px' }}>
                {activity.address && (
                  <p style={{ fontSize: 13, color: 'var(--ink-mute)', margin: '0 0 10px' }}>{activity.address}</p>
                )}
                {(() => {
                  const blurb = activity.blurb && activity.blurb.trim() !== activity.name.trim()
                    ? activity.blurb : reviewBlurb;
                  return blurb ? (
                    <p style={{ fontSize: 14.5, lineHeight: 1.55, color: 'var(--ink-soft)', margin: '0 0 16px' }}>
                      {blurb}
                    </p>
                  ) : null;
                })()}
                <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 22 }}>
                  {activity.tags.map((t, i) => <span key={i} className="chip">{t}</span>)}
                </div>
                <button className="btn coral full" onClick={signInToSave}>
                  Sign in to save it
                </button>
                <p className="soft" style={{ fontSize: 12, textAlign: 'center', marginTop: 12 }}>
                  A friend shared this with you on Larkle, a way to find things to do.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      <Toast msg={toast} onDone={clearToast}/>
    </div>
  );
}

function PublicHero({ activity }) {
  const url = activity._place?.images?.[0]?.url;
  if (url) {
    return <img src={url} alt={activity.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}/>;
  }
  const h = activity.hue;
  return <div style={{
    position: 'absolute', inset: 0,
    background: `linear-gradient(180deg, oklch(0.82 0.13 ${h}) 0%, oklch(0.70 0.18 ${h}) 55%, oklch(0.42 0.14 ${h + 6}) 100%)`,
  }}/>;
}

// ── Invite redemption page (/invite/:code) ──────────────────────────────────
function InvitePage({ code, authUser, onDone, toast, onToast, clearToast }) {
  const [status, setStatus] = React.useState('idle'); // idle | redeeming | done | error

  React.useEffect(() => {
    if (!authUser || status !== 'idle') return;
    setStatus('redeeming');
    apiRedeemInvite(code)
      .then(() => { setStatus('done'); onToast('You are now friends!'); setTimeout(onDone, 900); })
      .catch(() => setStatus('error'));
  }, [authUser, code]);

  return (
    <div className="app">
      <div className="app-stage">
        <div className="screen" style={{ background: 'var(--bg)' }}>
          <div className="scroll" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, padding: 32, textAlign: 'center' }}>
            <LarkleMark size={64} round/>
            {!authUser ? (
              <>
                <div className="serif" style={{ fontSize: 28, color: 'var(--terracotta)', lineHeight: 1.1 }}>
                  Someone wants you<br/>on Larkle.
                </div>
                <p className="soft" style={{ fontSize: 14, maxWidth: 280, margin: 0 }}>
                  Sign in and you'll be friends automatically.
                </p>
                <button className="btn coral" style={{ height: 46, padding: '0 32px' }}
                        onClick={() => {
                          trackEvent('sign_in_clicked', { source: 'invite' });
                          larkleSignIn().catch(err => onToast(signInErrorMessage(err)));
                        }}>
                  Sign in with Google
                </button>
              </>
            ) : status === 'error' ? (
              <>
                <div className="serif" style={{ fontSize: 26, color: 'var(--terracotta)' }}>That invite didn't work.</div>
                <p className="soft" style={{ fontSize: 14, margin: 0 }}>It may have expired. Ask your friend for a new link.</p>
                <button className="btn ghost" style={{ height: 42, padding: '0 24px' }} onClick={onDone}>Open the app</button>
              </>
            ) : (
              <div className="serif" style={{ fontSize: 24, color: 'var(--ink-mute)' }}>
                {status === 'done' ? 'Friends! 🎉' : 'Connecting you…'}
              </div>
            )}
          </div>
        </div>
      </div>
      <Toast msg={toast} onDone={clearToast}/>
    </div>
  );
}

export default function Root() {
  return (
    <BrowserRouter>
      <App/>
    </BrowserRouter>
  );
}
